import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * Rede embaixo das telas do painel.
 *
 * Sem isto (auditoria 360°, achado D10-08), um erro de renderização em
 * QUALQUER tela derrubava o painel inteiro pra tela branca — sem mensagem, sem
 * caminho de volta, e sem nada avisando que aconteceu. A dona só via "parou de
 * funcionar" e não tinha o que contar sobre o quê.
 *
 * Ela pediu isso com todas as letras em 23/08/2026: mostrar "esta tela falhou"
 * em vez de tela branca.
 *
 * Duas decisões que não são óbvias:
 *
 * - **O menu continua de pé.** O boundary embrulha só o miolo, não o layout,
 *   então dá pra ir pra outra tela sem recarregar a página. Tela branca cobria
 *   o menu junto e obrigava a digitar o endereço na mão.
 * - **O detalhe do erro fica escondido atrás de um toggle**, não sumido. Quem
 *   for consertar precisa da mensagem; quem só quer voltar a trabalhar não
 *   precisa ver stack trace.
 */

interface Props {
  children: ReactNode;
  /** Muda de valor a cada navegação — reseta o boundary ao trocar de tela. */
  resetKey?: string;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidUpdate(prev: Props): void {
    // Trocou de tela: limpa o erro pra não carimbar a tela nova com a falha da
    // anterior.
    if (prev.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[painel] tela quebrou', error, info.componentStack);
  }

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <section className="space-y-4">
        <div className="rounded-lg border border-sara-danger/40 bg-sara-surface p-6 space-y-3">
          <h2 className="text-lg font-semibold text-sara-danger">Esta tela falhou</h2>
          <p className="text-sm text-sara-text-muted max-w-prose">
            O resto do painel continua funcionando — dá pra ir pra outra tela pelo menu. Se isso se
            repetir sempre nesta tela, é defeito e precisa de conserto.
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              className="btn-primary text-sm"
              onClick={() => this.setState({ error: null })}
            >
              Tentar de novo
            </button>
            <button
              type="button"
              className="btn-ghost text-sm"
              onClick={() => window.location.reload()}
            >
              Recarregar a página
            </button>
          </div>
          <details className="text-xs text-sara-text-muted">
            <summary className="cursor-pointer">detalhe técnico</summary>
            <pre className="mt-2 overflow-x-auto whitespace-pre-wrap">{error.message}</pre>
          </details>
        </div>
      </section>
    );
  }
}
